import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Department, departmentLabelKey, type UpsertChecklistTemplateBody } from '@nbh/shared';
import { ChecklistStepEditor, type DraftStep } from './ChecklistStepEditor';
import type { AdminChecklistTemplate } from '../../lib/adminApi';
import { randomUuid } from '../../lib/uuid';

function blankStep(): DraftStep {
  return { key: randomUuid(), instruction: '', isRequired: true, requirePhoto: false };
}

export interface ChecklistTemplateBuilderProps {
  /** Present when editing; absent when creating. */
  template?: AdminChecklistTemplate;
  onSave: (body: UpsertChecklistTemplateBody) => Promise<void>;
  onCancel?: () => void;
}

/** Create or edit an SOP checklist (SPEC.md §5.4). */
export function ChecklistTemplateBuilder({
  template,
  onSave,
  onCancel,
}: ChecklistTemplateBuilderProps) {
  const { t } = useTranslation();

  const [title, setTitle] = useState(template?.title ?? '');
  const [department, setDepartment] = useState<Department>(
    (template?.department as Department) ?? Department.Maintenance,
  );
  const [steps, setSteps] = useState<DraftStep[]>(
    template?.items.map((item) => ({ key: item.id, ...item })) ?? [blankStep()],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patchStep(key: string, patch: Partial<DraftStep>) {
    setSteps((current) => current.map((step) => (step.key === key ? { ...step, ...patch } : step)));
  }

  function moveStep(index: number, direction: -1 | 1) {
    setSteps((current) => {
      const next = [...current];
      const target = index + direction;
      // Swap rather than splice-and-insert: adjacent moves are all the buttons offer.
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    const items = steps
      .map((step) => ({
        instruction: step.instruction.trim(),
        isRequired: step.isRequired,
        requirePhoto: step.requirePhoto,
      }))
      // Blank rows are scaffolding from clicking "add step", not real steps.
      .filter((step) => step.instruction.length > 0);

    if (title.trim().length < 3 || items.length === 0) {
      setError(t('adminChecklists.incomplete'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), department, items });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('adminChecklists.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-800 p-3">
      <div className="flex flex-wrap gap-2">
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('adminChecklists.titlePlaceholder')}
          aria-label={t('adminChecklists.title')}
          className="min-w-[180px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <select
          value={department}
          onChange={(event) => setDepartment(event.target.value as Department)}
          aria-label={t('adminChecklists.department')}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        >
          {Object.values(Department).map((value) => (
            <option key={value} value={value}>
              {t(departmentLabelKey[value])}
            </option>
          ))}
        </select>
      </div>

      <ul className="space-y-2">
        {steps.map((step, index) => (
          <ChecklistStepEditor
            key={step.key}
            step={step}
            index={index}
            total={steps.length}
            onChange={(patch) => patchStep(step.key, patch)}
            onMove={(direction) => moveStep(index, direction)}
            onRemove={() => setSteps((current) => current.filter((s) => s.key !== step.key))}
          />
        ))}
      </ul>

      <button
        type="button"
        onClick={() => setSteps((current) => [...current, blankStep()])}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded border border-dashed border-slate-600 bg-transparent px-3 text-sm text-slate-300"
      >
        <Plus className="size-4" aria-hidden="true" />
        {t('adminChecklists.addStep')}
      </button>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="min-h-[44px] flex-1 rounded bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          {busy ? t('adminChecklists.saving') : t('adminChecklists.save')}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[44px] rounded bg-transparent px-4 text-sm text-slate-400"
          >
            {t('adminChecklists.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}
